import { getSignedUrl } from '@/shared/requests/get-signed-url';

export const getColumnIndex = (letter: string) => {
  return letter.charCodeAt(0) - 'A'.charCodeAt(0);
};

export const getColumnLetter = (index: number) => {
  return String.fromCharCode(index + 'A'.charCodeAt(0));
};

export function toTwoDecimalString(input: string): string | null {
  const num = Number(input);
  if (isNaN(num)) return null;

  return num.toFixed(2);
}

type ExtractOptions = {
  extractFromSourceUrl?: boolean;
};

const extractFileNameFromUrl = (url: string): string => {
  if (!url || url.includes('previous_sessions')) return '';
  const urlWithoutQuery = url.split('?')[0];
  const fileName = urlWithoutQuery.substring(
    urlWithoutQuery.lastIndexOf('/') + 1
  );
  return decodeURIComponent(fileName);
};

// Extract file names and URLs from data as a Map<fileName, url>
export const extractUniqueFilesWithUrls = (
  obj: any,
  options: ExtractOptions = { extractFromSourceUrl: true },
  filesMap: Map<string, string> = new Map()
): Map<string, string> => {
  if (Array.isArray(obj)) {
    for (const item of obj) {
      extractUniqueFilesWithUrls(item, options, filesMap);
    }
  } else if (typeof obj === 'object' && obj !== null) {
    for (const key of Object.keys(obj)) {
      if (key === 'output') continue;

      const value = obj[key];

      if (
        (key === 'file_name' || key === 'fileName') &&
        typeof value === 'string' &&
        value
      ) {
        if (!filesMap.has(value)) {
          filesMap.set(value, '');
        }
      } else if (
        options.extractFromSourceUrl &&
        (key.endsWith('_url') ||
          key.endsWith('_urls') ||
          key === 'url' ||
          key === 'urls') &&
        value
      ) {
        if (typeof value === 'string') {
          const fileName = extractFileNameFromUrl(value);
          if (fileName && !filesMap.has(fileName)) {
            filesMap.set(fileName, value);
          }
        } else if (Array.isArray(value)) {
          for (const url of value) {
            if (typeof url === 'string') {
              const fileName = extractFileNameFromUrl(url);
              if (fileName && !filesMap.has(fileName)) {
                filesMap.set(fileName, url);
              }
            }
          }
        }
      } else if (typeof value === 'object' && value !== null) {
        extractUniqueFilesWithUrls(value, options, filesMap);
      }
    }
  }
  return filesMap;
};

export const getFilesUsedForSession = (agentData: any): Map<string, string> => {
  let filesMap: Map<string, string> = new Map();
  if (agentData?.agentType === 'risk_assessment') {
    if (agentData?.subType === 'fair_ra') {
      filesMap = extractUniqueFilesWithUrls(
        (agentData?.sessionData as any)?.fair_ra,
        { extractFromSourceUrl: true }
      );
    } else {
      filesMap = new Map(
        Array.from(
          (
            agentData?.mainData?.sourceFilesUrls as Map<string, { url: string }>
          ).entries()
        ).map(([key, value]) => [key, value.url])
      );
    }
  } else if (agentData?.agentType === 'multi_file_vendor_assessment') {
    // First check if files exist in metadata
    const vendorProfileStep = agentData?.stepData?.[0];
    if (vendorProfileStep?.data?.metadata?.files && Array.isArray(vendorProfileStep.data.metadata.files)) {
      filesMap = new Map(
        vendorProfileStep.data.metadata.files.map((file: any) => [
          file.file_name,
          file.file_url
        ])
      );
    } else {
      // Fallback to old logic if metadata is not found
      filesMap = extractUniqueFilesWithUrls(agentData?.stepData[0]?.data as any, {
        extractFromSourceUrl: true,
      });
    }
  } else if (agentData?.agentType === 'questionnaire') {
    filesMap = extractUniqueFilesWithUrls(agentData?.sessionData, {
      extractFromSourceUrl: true,
    });
  } else {
    filesMap = agentData?.mainData?.sourceFilesUrls || new Map();
  }

  return filesMap;
};

// Download function that checks URL validity, refreshes signed URL if expired, and downloads the file
export const downloadFileWithCheck = async (
  fileName: string,
  initialUrl: string,
  showErrorToast: (msg: string) => void
) => {
  let currentUrl = initialUrl;

  try {
    // Try HEAD request to check if URL is valid
    try {
      const headResponse = await fetch(currentUrl, { method: 'HEAD' });
      if (!headResponse.ok) {
        // If HEAD response is not OK, treat URL as stale
        throw new Error('Stale URL detected via HEAD response');
      }
    } catch (headError) {
      // HEAD request failed (network error, CORS, or stale URL)
      // Try to get a refreshed signed URL
      try {
        const signedUrls = await getSignedUrl({
          stale_urls: [currentUrl],
          max_age: 86400,
        });
        if (!signedUrls || signedUrls.length === 0) {
          throw new Error('Failed to refresh signed URL');
        }
        currentUrl = signedUrls[0];
      } catch (refreshError) {
        // Failed to refresh signed URL
        throw refreshError;
      }
    }

    // Now try to download the file using the valid/refreshed URL
    const downloadResponse = await fetch(currentUrl);
    if (!downloadResponse.ok) {
      throw new Error('Failed to download file');
    }

    const blob = await downloadResponse.blob();
    const url = window.URL.createObjectURL(blob);

    // Trigger download
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();

    // Cleanup
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    showErrorToast('Failed to download file. Please try again later.');
  }
};


export const extractAgentId = (url: string) => {
  const match = url.match(/\/agent\/([a-f0-9]{32})/);
  return match ? match[1] : null;
}